import { useLayoutEffect, useRef, useState } from 'react';

/** Starting placeholder count, before this hook measures how many actually
    fit. Also the floor it never goes below, and the count used if measuring
    can't produce a usable number. Deliberately not a "real" count -- the
    actual feed length is unknown until the RSS query resolves. */
export const DEFAULT_SKELETON_ITEM_COUNT = 8;

/** Hard ceiling on the measured count. Nothing sane reaches this (a 4K
    display in top-stories' three-column desktop grid needs about 30), so it
    exists purely to keep a bad measurement from turning into thousands of
    DOM nodes. */
const SKELETON_ITEM_LIMIT = 60;

/** Below this, a measured row height is not believable -- a near-zero height
    (measured mid-layout, or with fonts still swapping) divided into the
    viewport is what would produce an absurd count. Both callers' real rows
    are comfortably above this (top-stories: `aspect-ratio: 5/2` on a
    >=120px-wide item; daily-news: a title line plus several text lines). */
const MIN_MEASURABLE_ROW_HEIGHT = 24;

/**
 * How many skeleton rows it takes to reach the bottom of the viewport.
 *
 * Shared by poets' two RSS widgets, top-stories and daily-news -- both hit
 * the same defect a fixed skeleton count has: a fixed number of rows is one
 * number for every screen, so it's right for at most one breakpoint and
 * leaves the rest of the section blank on any taller viewport (reads as
 * "that's all there is" rather than "still loading", then jumps when the
 * real feed arrives and fills the space).
 *
 * Both numbers this needs are already decided by each widget's own CSS, at
 * breakpoints this hook has no business restating, so it measures rather
 * than assumes: one rendered row's own height, and the column count read
 * straight off the resolved `grid-template-columns` of the container the
 * caller points `containerRef` at. That keeps it correct at every
 * breakpoint -- desktop, tablet, and mobile, including top-stories' column
 * count changing between them -- with no per-breakpoint pixel constants
 * here to drift out of sync with either widget's own module CSS.
 *
 * `Math.max(DEFAULT_SKELETON_ITEM_COUNT, ...)` so this only ever *adds* rows
 * to fill space; a short viewport keeps the starting count instead of
 * shrinking to a single stubby row.
 */
export function useViewportFillingCount() {
	const containerRef = useRef<HTMLDivElement>(null);
	const [count, setCount] = useState(DEFAULT_SKELETON_ITEM_COUNT);

	// Layout effect, not a plain one: this runs before the browser paints, so
	// the reader sees the filled skeleton directly rather than the short one
	// for a frame and then a second, taller version replacing it.
	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const measure = () => {
			const row = container.firstElementChild;
			if (!row) {
				return;
			}
			// The item *container* (e.g. `.feed-item-container`), not the
			// item itself -- its padding is part of what a row occupies.
			const rowHeight = row.getBoundingClientRect().height;
			const columns = getComputedStyle(container)
				.gridTemplateColumns.split(' ')
				.filter(Boolean).length;
			// Distance from the top of the grid to the bottom of the window,
			// so whatever heading content sits above it is already accounted
			// for. The section is its own scroll container in both callers,
			// so overshooting slightly just costs a row below the fold.
			const available =
				window.innerHeight - container.getBoundingClientRect().top;
			if (
				rowHeight < MIN_MEASURABLE_ROW_HEIGHT ||
				columns <= 0 ||
				available <= 0
			) {
				return;
			}
			setCount(
				Math.min(
					SKELETON_ITEM_LIMIT,
					Math.max(
						DEFAULT_SKELETON_ITEM_COUNT,
						Math.ceil(available / rowHeight) * columns,
					),
				),
			);
		};

		measure();
		// Covers a desktop window resize and, on mobile, rotation -- both
		// change the column count and the height to fill at once.
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, []);

	return { containerRef, count };
}
